import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { Container, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Box, Divider, Button, IconButton, Tooltip } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import HistoryIcon from '@mui/icons-material/History';

import useStyles from './styles';
import SearchForm from './searchForm';
import ShowMore from 'components/ShowMore';
import Pagination from 'components/Pagination';
import ListHeader from 'components/ListHeader';

import { healthBenefitPaymentRequestState } from 'atoms/HealthBenefitState';
import { listHealthBenefitPaymentRequests, updateHealthBenefitPaymentStatus, updateHealthBenefitRemark } from 'repositories/HttpRepository';
import DialogApprove from './DialogApprove';
import RequestHistory from './RequestHistory';
import DialogConfirm from './DialogConfirm';

const fields = [
  { id: 'driver_id', numeric: false, disablePadding: false, label: 'Driver Id' },
  {
    id: 'driver_name',
    numeric: false,
    disablePadding: false,
    label: 'Driver name',
    isDisableSort: true,
  },
  {
    id: 'remark',
    numeric: false,
    disablePadding: false,
    label: 'Remark',
    isDisableSort: true,
  },
  {
    id: 'payout_amount',
    numeric: false,
    disablePadding: false,
    label: 'Payout requested',
    isDisableSort: true,
  },
  {
    id: 'approved_payout',
    numeric: false,
    disablePadding: false,
    label: 'Payout amount',
  },
  {
    id: 'files',
    numeric: false,
    disablePadding: false,
    label: 'Attached files',
    isDisableSort: true,
  },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
  {
    id: 'created_ts',
    numeric: false,
    disablePadding: false,
    label: 'Days left',
    isDisableSort: false,
  },
  {
    id: 'created_ts',
    numeric: false,
    disablePadding: false,
    label: 'Created date',
  },
  {
    id: 't.start_time_ts',
    numeric: false,
    disablePadding: false,
    label: 'Period',
    isDisableSort: true,
  },
  { id: 'actions', numeric: false, isDisableSort: true },
];

const HealthBenefitPaymentRequest = () => {
  const { classes } = useStyles();
  const [healthBenefits, setHealthBenefits] = useRecoilState(healthBenefitPaymentRequestState);
  const [isConfirmApprove, setIsConfirmApprove] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isHistory, setIsHistory] = useState(false);
  const [isConfirmReject, setIsConfirmReject] = useState(false);
  const [isConfirmInfo, setIsConfirmInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { items, page, size, desc, order_by, total, isListing, search_text, status, total_driver, sum_payout_amount, years, quarters } = healthBenefits;

  const getList = (params) => {
    // Convert "ALL" to empty array for API
    const processedYears = years?.includes('ALL') ? [] : years;
    const processedQuarters = quarters?.includes('ALL') ? [] : quarters;

    const queryParams = {
      size,
      page,
      search_text,
      status,
      order_by,
      desc,
      years: processedYears,
      quarters: processedQuarters,
    };

    if (!years || years.length === 0 || !quarters || quarters.length === 0) return;

    // Set loading state
    setHealthBenefits((previous) => ({
      ...previous,
      isListing: true,
    }));

    listHealthBenefitPaymentRequests({ ...queryParams, ...params })
      .then((response) => {
        if (response.ok) {
          const data = response.data;
          setHealthBenefits((previous) => ({
            ...previous,
            items: data.items,
            page: data.page,
            size: data.size,
            total: data.total,
            total_driver: data.total_driver,
            sum_payout_amount: data.sum_payout_amount,
            isListing: false,
          }));
        } else {
          setHealthBenefits((previous) => ({
            ...previous,
            isListing: false,
          }));
        }
      })
      .catch(() => {
        setHealthBenefits((previous) => ({
          ...previous,
          isListing: false,
        }));
      });
  };

  useEffect(() => {
    getList();
  }, [search_text, status, order_by, desc, page, years, quarters]);

  const handleChangePage = (event, newPage) => {
    setHealthBenefits({ ...healthBenefits, page: newPage });
  };

  const handleRequestSort = (event, property) => {
    const isDesc = order_by === property && desc;
    setHealthBenefits({
      ...healthBenefits,
      desc: !isDesc,
      order_by: property,
    });
  };

  const handleAgreeReject = (id, remark) => {
    setIsSubmitting(true);
    const params = { remark: remark };

    updateHealthBenefitPaymentStatus(id, 'REJECTED', params).then((res) => {
      setIsSubmitting(false);
      if (res.ok) {
        toast.success('Rejected successfully !');
        getList();
        // Close dialog after successful submission
        setIsConfirmReject(false);
        setSelectedItem(null);
      } else {
        toast.error('Server is having problem, please try again later.');
      }
    });
  };

  const handleAddRemark = async (id, remark) => {
    setIsSubmitting(true);
    updateHealthBenefitRemark(id, { remark }).then((response) => {
      setIsSubmitting(false);
      if (!response.ok) return toast.error(get(response, 'data.message', 'Server is having problem, please try again later.'));

      getList();
      toast.success('Update remark successfully!');
      // Close dialog after successful submission
      setIsConfirmInfo(false);
      setSelectedItem(null);
    });
  };

  const handleConfirm = (id, val, setField) => {
    setSelectedItem(id);
    setField(val);
  };

  const handleToggleEvent = (id, val) => {
    setSelectedItem(id);
    setIsHistory(val);
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      <SearchForm initValue={search_text} placeholder="Search for driver ID, driver name" />
      <Paper className={classes.paper}>
        {isListing && <LinearProgress />}
        <TableContainer>
          <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
            <ListHeader fields={fields} desc={desc} orderBy={order_by} onRequestOrder={handleRequestSort} rowCount={items.length} headerDisableUppercase={true} />
            <TableBody>
              {items &&
                items.map((item, index) => {
                  const labelId = `dsp-table-item-${index}`;
                  const period = item?.start_time_ts ? `${moment.utc(item.start_time_ts).year()} - Quarter ${moment.utc(item.start_time_ts).quarter()}` : null;
                  const files = item?.files.map((file, index) => (
                    <a key={`${file.path}-${index}`} target="_blank" className="file" href={file.path}>
                      <AttachmentIcon />
                    </a>
                  ));

                  // Check if action already taken for this driver/quarter/year
                  const itemYear = item?.start_time_ts ? moment.utc(item.start_time_ts).year() : null;
                  const itemQuarter = item?.start_time_ts ? moment.utc(item.start_time_ts).quarter() : null;

                  // Only show "Action already taken" if this request is SUBMITTED/UNDER_REVIEW
                  // AND there exists an APPROVED/REJECTED request for the same driver/quarter/year
                  // that was created BEFORE this request
                  const isDuplicate =
                    ['SUBMITTED', 'UNDER_REVIEW'].includes(item.status) &&
                    items.some((prevItem) => {
                      const prevYear = prevItem?.start_time_ts ? moment.utc(prevItem.start_time_ts).year() : null;
                      const prevQuarter = prevItem?.start_time_ts ? moment.utc(prevItem.start_time_ts).quarter() : null;

                      return (
                        prevItem.id !== item.id &&
                        prevItem.driver_id === item.driver_id &&
                        ['APPROVED', 'REJECTED'].includes(prevItem.status) &&
                        prevYear === itemYear &&
                        prevQuarter === itemQuarter &&
                        // Check if prevItem was created BEFORE current item
                        moment(prevItem.created_ts).isBefore(moment(item.created_ts))
                      );
                    });

                  // Check if this is the latest request for this driver/quarter/year
                  const isNotLatest = items.some((otherItem) => {
                    if (otherItem.id === item.id) return false;

                    const otherYear = otherItem?.start_time_ts ? moment.utc(otherItem.start_time_ts).year() : null;
                    const otherQuarter = otherItem?.start_time_ts ? moment.utc(otherItem.start_time_ts).quarter() : null;

                    // Same driver, same quarter/year
                    if (otherItem.driver_id === item.driver_id && otherYear === itemYear && otherQuarter === itemQuarter) {
                      // Compare created_ts to find the latest
                      return moment(otherItem.created_ts).isAfter(moment(item.created_ts));
                    }
                    return false;
                  });

                  const isAlreadyProcessed = ['APPROVED', 'REJECTED'].includes(item.status);

                  // Disable buttons if already processed OR not the latest
                  const disableButtons = isAlreadyProcessed || isNotLatest;

                  // Disable row styling ONLY if not the latest (old records)
                  // If it's the latest record with APPROVED/REJECTED status, only disable buttons, not row
                  const disableRowStyling = isNotLatest;

                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={item.id}
                      sx={{
                        backgroundColor: disableRowStyling ? '#f5f5f5' : 'inherit',
                        opacity: disableRowStyling ? 0.6 : 1,
                        '& .MuiTableCell-root': {
                          color: disableRowStyling ? '#9e9e9e' : 'inherit',
                        },
                      }}
                    >
                      <TableCell>{item.driver_id}</TableCell>
                      <TableCell>{item.driver_name}</TableCell>
                      <TableCell>
                        <ShowMore text={item.remark || ''} length={30} />
                      </TableCell>
                      <TableCell>{`$${item.payout_amount}`}</TableCell>
                      <TableCell>{['REJECTED', 'UNDER_REVIEW'].includes(item.status) ? '$0' : `$${item.approved_payout}`}</TableCell>
                      <TableCell>{files}</TableCell>
                      <TableCell>{item.status}</TableCell>
                      <TableCell>{item.left_day_before_expired > 0 ? item.left_day_before_expired : '0'}</TableCell>
                      <TableCell>{moment.utc(item.created_ts).local().format('YYYY-MM-DD HH:mm')}</TableCell>
                      <TableCell>{period}</TableCell>
                      <TableCell style={{ minWidth: 250 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <Button disableElevation disabled={disableButtons} color="success" variant="contained" size="small" onClick={() => handleConfirm(item, true, setIsConfirmApprove)}>
                              Approve
                            </Button>
                            <Button disableElevation disabled={disableButtons} color="error" variant="contained" size="small" onClick={() => handleConfirm(item, true, setIsConfirmReject)}>
                              Reject
                            </Button>
                            <Button disableElevation disabled={disableButtons} color="success" variant="outlined" size="small" onClick={() => handleConfirm(item, true, setIsConfirmInfo)}>
                              Info
                            </Button>
                            <Tooltip title="Request History">
                              <IconButton
                                size="small"
                                style={{
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '5px 10px',
                                }}
                                onClick={() => handleToggleEvent(item, true)}
                              >
                                <HistoryIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {isDuplicate && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#d32f2f',
                                fontWeight: 500,
                              }}
                            >
                              Action already taken for this quarter
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} style={{ textAlign: 'center', backgroundColor: '#fff' }}>
                    Record not found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination total={total} size={size} onChange={handleChangePage} forceUpdatePage={page - 1} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 1.5rem 0.5rem 1.5rem',
            gap: '0.5rem',
          }}
        >
          <Typography variant="subtitle2">
            Number of drivers: <strong>{total_driver}</strong>
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography variant="subtitle2">
            Total payout amount: <strong>${sum_payout_amount}</strong>
          </Typography>
        </Box>
      </Paper>

      {isConfirmApprove && <DialogApprove item={selectedItem} isOpen={isConfirmApprove} onClose={() => handleConfirm(null, false, setIsConfirmApprove)} getList={getList} />}
      {isConfirmReject && (
        <DialogConfirm
          title={'Do you want to reject this request?'}
          isOpen={isConfirmReject}
          onClose={() => handleConfirm(null, false, setIsConfirmReject)}
          isSubmitting={isSubmitting}
          handleSubmit={(remark) => handleAgreeReject(selectedItem.id, remark)}
          btnSubmit={'Reject'}
          color={'error'}
        />
      )}
      {isConfirmInfo && (
        <DialogConfirm
          title={'Additional Information Required'}
          isOpen={isConfirmInfo}
          onClose={() => handleConfirm(null, false, setIsConfirmInfo)}
          isSubmitting={isSubmitting}
          handleSubmit={(remark) => handleAddRemark(selectedItem.id, remark)}
          btnSubmit={'Submit'}
          color={'success'}
          showWarning={true}
        />
      )}
      {isHistory && <RequestHistory item={selectedItem} isOpen={isHistory} onClose={() => handleToggleEvent(null, false)} />}
    </Container>
  );
};
export default React.memo(HealthBenefitPaymentRequest);
